/**
 * LandmarkPicker - Visual overlay for selecting body landmarks
 *
 * Renders an interactive overlay on top of video that shows all
 * detected body landmarks as clickable dots. Users can click to
 * select source and target landmarks for spatial goal constraints.
 *
 * Features:
 * - Color-coded visibility (green=reliable, yellow=acceptable, red=occluded)
 * - Snap-to-nearest-landmark behavior
 * - Smooth hover/selection animations
 * - Connection line preview when selecting target
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/solid';
import { cn } from '@/utils/cn';
import {
  findNearestLandmark,
  getAllLandmarkPositions,
  getLandmarkLabel,
  type PoseData,
  type LandmarkPosition,
} from '@/utils/landmarkDetection';
import { LANDMARK_CONFIG, TIMEOUTS } from '@/utils/constants';

interface LandmarkPickerProps {
  poseData: PoseData;
  videoWidth: number;
  videoHeight: number;
  containerRef: React.RefObject<HTMLDivElement | null>;
  mode: 'idle' | 'selecting-source' | 'selecting-target';
  sourceLandmark?: string;
  targetLandmark?: string;
  onSourceSelect: (landmark: string, landmarkIndex?: number) => void;
  onTargetSelect: (landmark: string, landmarkIndex?: number) => void;
  onCancel: () => void;
}

// CSS for animations (injected into component)
const PICKER_STYLES = `
  @keyframes landmark-pulse {
    0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
    50% { transform: translate(-50%, -50%) scale(1.3); opacity: 0.7; }
  }

  @keyframes snap-ring {
    0% { transform: translate(-50%, -50%) scale(0.5); opacity: 1; }
    100% { transform: translate(-50%, -50%) scale(2); opacity: 0; }
  }

  @keyframes selected-glow {
    0%, 100% { box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.4), 0 0 20px rgba(59, 130, 246, 0.3); }
    50% { box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.2), 0 0 30px rgba(59, 130, 246, 0.4); }
  }

  @keyframes instruction-slide {
    0% { transform: translateY(-100%); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
  }

  .landmark-dot {
    transform: translate(-50%, -50%);
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .landmark-dot:hover {
    transform: translate(-50%, -50%) scale(1.6);
    z-index: 100 !important;
  }

  .landmark-dot.selected-source {
    animation: selected-glow 1.5s ease-in-out infinite;
  }

  .snap-indicator {
    animation: snap-ring 0.4s ease-out forwards;
  }

  .landmark-tooltip {
    transform: translateX(-50%);
    animation: fadeIn 0.15s ease-out;
  }

  @keyframes fadeIn {
    0% { opacity: 0; transform: translateX(-50%) translateY(4px); }
    100% { opacity: 1; transform: translateX(-50%) translateY(0); }
  }
`;

export default function LandmarkPicker({
  poseData,
  videoWidth,
  videoHeight,
  containerRef,
  mode,
  sourceLandmark,
  targetLandmark,
  onSourceSelect,
  onTargetSelect,
  onCancel,
}: LandmarkPickerProps) {
  const [hoveredLandmark, setHoveredLandmark] = useState<string | null>(null);
  const [cursorPosition, setCursorPosition] = useState<{ x: number; y: number } | null>(null);
  const [snapIndicator, setSnapIndicator] = useState<{ x: number; y: number } | null>(null);
  const [containerRect, setContainerRect] = useState<DOMRect | null>(null);

  // Get all landmark positions
  const landmarks = useMemo(() => {
    return getAllLandmarkPositions(poseData, videoWidth, videoHeight);
  }, [poseData, videoWidth, videoHeight]);

  // Find source and target landmark positions
  const sourceLandmarkPos = useMemo(() => {
    if (!sourceLandmark) return null;
    return landmarks.find((l) => l.name === sourceLandmark);
  }, [landmarks, sourceLandmark]);

  const targetLandmarkPos = useMemo(() => {
    if (!targetLandmark) return null;
    return landmarks.find((l) => l.name === targetLandmark);
  }, [landmarks, targetLandmark]);

  // Update container rect on mount and resize
  useEffect(() => {
    const updateRect = () => {
      if (containerRef.current) {
        setContainerRect(containerRef.current.getBoundingClientRect());
      }
    };

    updateRect();
    window.addEventListener('resize', updateRect);
    return () => window.removeEventListener('resize', updateRect);
  }, [containerRef]);

  // Convert video coordinates to container coordinates
  const videoToContainer = useCallback(
    (x: number, y: number) => {
      if (!containerRect) return { x: 0, y: 0 };
      const scaleX = containerRect.width / videoWidth;
      const scaleY = containerRect.height / videoHeight;
      return {
        x: x * scaleX,
        y: y * scaleY,
      };
    },
    [containerRect, videoWidth, videoHeight]
  );

  // Convert container coordinates to video coordinates
  const containerToVideo = useCallback(
    (clientX: number, clientY: number) => {
      if (!containerRect) return { x: 0, y: 0 };
      const x = clientX - containerRect.left;
      const y = clientY - containerRect.top;
      const scaleX = videoWidth / containerRect.width;
      const scaleY = videoHeight / containerRect.height;
      return {
        x: x * scaleX,
        y: y * scaleY,
      };
    },
    [containerRect, videoWidth, videoHeight]
  );

  // Handle mouse move for cursor tracking
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!containerRect) return;
      const containerX = e.clientX - containerRect.left;
      const containerY = e.clientY - containerRect.top;
      setCursorPosition({ x: containerX, y: containerY });
    },
    [containerRect]
  );

  // Handle click on overlay
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (mode === 'idle') return;

      const videoCoords = containerToVideo(e.clientX, e.clientY);
      const nearest = findNearestLandmark(
        videoCoords.x,
        videoCoords.y,
        poseData,
        videoWidth,
        videoHeight,
        LANDMARK_CONFIG.SNAP_RADIUS,
        LANDMARK_CONFIG.MIN_VISIBILITY
      );

      if (nearest) {
        // Show snap indicator
        const containerPos = videoToContainer(nearest.x, nearest.y);
        setSnapIndicator(containerPos);
        setTimeout(() => setSnapIndicator(null), TIMEOUTS.SNAP_INDICATOR);

        if (mode === 'selecting-source') {
          onSourceSelect(nearest.name, nearest.index);
        } else if (mode === 'selecting-target') {
          onTargetSelect(nearest.name, nearest.index);
        }
      }
    },
    [mode, containerToVideo, videoToContainer, poseData, videoWidth, videoHeight, onSourceSelect, onTargetSelect]
  );

  // Get dot styles based on visibility and selection state
  const getDotStyles = (landmark: LandmarkPosition) => {
    const isSource = landmark.name === sourceLandmark;
    const isTarget = landmark.name === targetLandmark;
    const isHovered = landmark.name === hoveredLandmark;
    const isClickable = landmark.visibilityCategory !== 'low';

    let size = 14;
    let bgColor = '#22c55e'; // green
    let borderStyle: React.CSSProperties['borderStyle'] = 'solid';
    let borderColor = 'white';

    if (landmark.visibilityCategory === 'medium') {
      size = 12;
      bgColor = '#eab308'; // yellow
      borderStyle = 'dashed';
    } else if (landmark.visibilityCategory === 'low') {
      size = 10;
      bgColor = '#ef4444'; // red
      borderStyle = 'dotted';
    }

    if (isSource) {
      bgColor = '#3b82f6'; // blue
      borderColor = 'white';
      size = 18;
    }

    if (isTarget) {
      bgColor = '#3b82f6'; // blue
      borderColor = 'white';
      size = 18;
    }

    if (isHovered && isClickable) {
      size = 22;
    }

    return {
      size,
      bgColor,
      borderStyle,
      borderColor,
      isClickable,
      isSource,
      isTarget,
    };
  };

  // Don't render if mode is idle or no container
  if (mode === 'idle' || !containerRef.current) {
    return null;
  }

  const overlayContent = (
    <>
      {/* Inject styles */}
      <style>{PICKER_STYLES}</style>

      {/* Semi-transparent overlay */}
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-[1px]"
        style={{ zIndex: 40 }}
        onMouseMove={handleMouseMove}
        onClick={handleOverlayClick}
      />

      {/* Instructions Banner */}
      <div
        className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-gray-900/95 to-gray-900/80 backdrop-blur-sm border-b border-white/10"
        style={{
          zIndex: 60,
          animation: 'instruction-slide 0.25s ease-out',
        }}
      >
        <div className="flex items-center gap-3">
          {/* Mode indicator dot */}
          <div
            className={cn(
              'w-3 h-3 rounded-full',
              mode === 'selecting-source' ? 'bg-amber-400 animate-pulse' : 'bg-blue-400 animate-pulse'
            )}
          />

          {/* Instructions text */}
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-white tracking-wide">
              {mode === 'selecting-source' ? 'SELECT SOURCE POINT' : 'SELECT TARGET POINT'}
            </span>
            <span className="text-xs text-white/70">
              {mode === 'selecting-source'
                ? 'Click on a body landmark to set the starting point'
                : 'Click on a body landmark to set the ending point'}
            </span>
          </div>
        </div>

        {/* Cancel button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onCancel();
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-md transition-all duration-150"
        >
          <XMarkIcon className="w-4 h-4" />
          Cancel
        </button>
      </div>

      {/* SVG layer for connection line */}
      <svg
        className="absolute inset-0 pointer-events-none"
        style={{ zIndex: 45 }}
        width="100%"
        height="100%"
      >
        {/* Line from source to cursor (when selecting target) */}
        {mode === 'selecting-target' && sourceLandmarkPos && cursorPosition && (
          <line
            x1={videoToContainer(sourceLandmarkPos.x, sourceLandmarkPos.y).x}
            y1={videoToContainer(sourceLandmarkPos.x, sourceLandmarkPos.y).y}
            x2={cursorPosition.x}
            y2={cursorPosition.y}
            stroke="#3b82f6"
            strokeWidth="2"
            strokeDasharray="6,4"
            strokeLinecap="round"
            opacity="0.8"
          />
        )}

        {/* Solid line between source and target (when both selected) */}
        {sourceLandmarkPos && targetLandmarkPos && (
          <line
            x1={videoToContainer(sourceLandmarkPos.x, sourceLandmarkPos.y).x}
            y1={videoToContainer(sourceLandmarkPos.x, sourceLandmarkPos.y).y}
            x2={videoToContainer(targetLandmarkPos.x, targetLandmarkPos.y).x}
            y2={videoToContainer(targetLandmarkPos.x, targetLandmarkPos.y).y}
            stroke="#3b82f6"
            strokeWidth="3"
            strokeLinecap="round"
            filter="drop-shadow(0 0 4px rgba(59, 130, 246, 0.5))"
          />
        )}
      </svg>

      {/* Landmark dots */}
      {landmarks.map((landmark) => {
        const styles = getDotStyles(landmark);
        const pos = videoToContainer(landmark.x, landmark.y);

        return (
          <div
            key={landmark.index}
            className={cn(
              'landmark-dot absolute rounded-full border-2 flex items-center justify-center',
              styles.isClickable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
              styles.isSource && 'selected-source'
            )}
            style={{
              left: pos.x,
              top: pos.y,
              width: styles.size,
              height: styles.size,
              backgroundColor: styles.bgColor,
              borderColor: styles.borderColor,
              borderStyle: styles.borderStyle,
              zIndex: styles.isSource || styles.isTarget ? 55 : 50,
            }}
            onMouseEnter={() => styles.isClickable && setHoveredLandmark(landmark.name)}
            onMouseLeave={() => setHoveredLandmark(null)}
            onClick={(e) => {
              e.stopPropagation();
              if (!styles.isClickable) return;

              // Show snap indicator
              setSnapIndicator(pos);
              setTimeout(() => setSnapIndicator(null), TIMEOUTS.SNAP_INDICATOR);

              if (mode === 'selecting-source') {
                onSourceSelect(landmark.name, landmark.index);
              } else if (mode === 'selecting-target') {
                onTargetSelect(landmark.name, landmark.index);
              }
            }}
          >
            {/* Checkmark for selected landmarks */}
            {(styles.isSource || styles.isTarget) && (
              <CheckIcon className="w-3 h-3 text-white" />
            )}

            {/* X pattern for low visibility */}
            {landmark.visibilityCategory === 'low' && (
              <XMarkIcon className="w-2.5 h-2.5 text-white/80" />
            )}
          </div>
        );
      })}

      {/* Hover tooltip */}
      {hoveredLandmark && (
        <div
          className="landmark-tooltip absolute pointer-events-none"
          style={{
            left: videoToContainer(
              landmarks.find((l) => l.name === hoveredLandmark)?.x || 0,
              landmarks.find((l) => l.name === hoveredLandmark)?.y || 0
            ).x,
            top:
              videoToContainer(
                landmarks.find((l) => l.name === hoveredLandmark)?.x || 0,
                landmarks.find((l) => l.name === hoveredLandmark)?.y || 0
              ).y - 45,
            zIndex: 100,
          }}
        >
          <div className="bg-gray-900/95 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-md shadow-lg border border-white/10 whitespace-nowrap">
            <div className="font-semibold">{getLandmarkLabel(hoveredLandmark)}</div>
            <div className="text-white/60 text-[10px]">
              {Math.round((landmarks.find((l) => l.name === hoveredLandmark)?.visibility || 0) * 100)}% visible
            </div>
          </div>
          {/* Tooltip arrow */}
          <div
            className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0"
            style={{
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid rgba(17, 24, 39, 0.95)',
            }}
          />
        </div>
      )}

      {/* Snap indicator ring */}
      {snapIndicator && (
        <div
          className="snap-indicator absolute rounded-full border-2 border-blue-400 pointer-events-none"
          style={{
            left: snapIndicator.x,
            top: snapIndicator.y,
            width: 24,
            height: 24,
            zIndex: 100,
          }}
        />
      )}

      {/* Selection status footer */}
      <div
        className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-6 px-4 py-2.5 bg-gradient-to-t from-gray-900/90 to-gray-900/70 backdrop-blur-sm border-t border-white/10"
        style={{ zIndex: 60 }}
      >
        <div className="flex items-center gap-2">
          <div
            className={cn(
              'w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all',
              sourceLandmark
                ? 'bg-blue-500 border-white'
                : 'bg-transparent border-white/40'
            )}
          >
            {sourceLandmark && <CheckIcon className="w-2.5 h-2.5 text-white" />}
          </div>
          <span className="text-xs text-white/80">
            From:{' '}
            <span className={sourceLandmark ? 'text-blue-400 font-medium' : 'text-white/50'}>
              {sourceLandmark ? getLandmarkLabel(sourceLandmark) : 'Not set'}
            </span>
          </span>
        </div>

        <div className="w-px h-4 bg-white/20" />

        <div className="flex items-center gap-2">
          <div
            className={cn(
              'w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all',
              targetLandmark
                ? 'bg-blue-500 border-white'
                : 'bg-transparent border-white/40'
            )}
          >
            {targetLandmark && <CheckIcon className="w-2.5 h-2.5 text-white" />}
          </div>
          <span className="text-xs text-white/80">
            To:{' '}
            <span className={targetLandmark ? 'text-blue-400 font-medium' : 'text-white/50'}>
              {targetLandmark ? getLandmarkLabel(targetLandmark) : 'Not set'}
            </span>
          </span>
        </div>
      </div>
    </>
  );

  // Render into the container using portal
  return createPortal(overlayContent, containerRef.current);
}
